/**
 * Light / dark theme preference.
 *
 * Three states, not two: "follow the OS" is a real answer and is the default,
 * so a machine that flips to dark at sunset takes the app with it. An explicit
 * choice pins the app and outranks the OS from then on.
 *
 * The preference is a per-device UI setting, so it lives in localStorage rather
 * than in the synced config document — a phone on dark and a desktop on light
 * is the expected arrangement, not a conflict to resolve.
 */

import { create } from 'zustand';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/**
 * Also read by the pre-paint script in `index.html`, which applies the theme
 * before React mounts so a reload never flashes the wrong one. If this key or
 * the `dark` class name changes, that script changes in the same commit.
 */
export const THEME_KEY = 'expenses.ui.theme';

const PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system'];

/** Mobile browser chrome, matched to the app header in each theme. */
const THEME_COLOR: Record<ResolvedTheme, string> = { light: '#0f172a', dark: '#020617' };

function darkQuery(): MediaQueryList {
  return window.matchMedia('(prefers-color-scheme: dark)');
}

function storedPreference(): ThemePreference {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(THEME_KEY);
  } catch {
    // Private mode with storage denied — fall through to the OS setting.
  }
  return PREFERENCES.includes(stored as ThemePreference) ? (stored as ThemePreference) : 'system';
}

function resolve(preference: ThemePreference): ResolvedTheme {
  if (preference !== 'system') return preference;
  return darkQuery().matches ? 'dark' : 'light';
}

function apply(theme: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  /*
   * `color-scheme` is what native widgets read — checkbox glyphs, the select
   * dropdown panel, the scrollbar gutter. Tailwind's `dark:` variants never
   * reach any of those, so without this line a dark page keeps white popups.
   */
  root.style.colorScheme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme]);
}

interface ThemeState {
  preference: ThemePreference;
  /** What is actually on screen — `preference` with 'system' already resolved. */
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

export const useThemeStore = create<ThemeState>((set) => {
  const preference = storedPreference();
  const resolved = resolve(preference);

  // Idempotent with the pre-paint script; repeated so the app is still correct
  // if that script is ever stripped from the shell.
  apply(resolved);

  // An OS switch has to reach a window that is already open, otherwise
  // "follow the system" only holds until the next sunset.
  darkQuery().addEventListener('change', (event) => {
    if (useThemeStore.getState().preference !== 'system') return;
    const next: ResolvedTheme = event.matches ? 'dark' : 'light';
    apply(next);
    set({ resolved: next });
  });

  return {
    preference,
    resolved,

    setPreference(next) {
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch {
        // Storage denied: the choice holds for this session only.
      }
      const theme = resolve(next);
      apply(theme);
      set({ preference: next, resolved: theme });
    },
  };
});

/** The theme charts have to draw against — see `ChartsView`. */
export const useResolvedTheme = (): ResolvedTheme => useThemeStore((state) => state.resolved);
